import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { Icon, Div, Text, Input } from '@pinpoint/design-kit';

import { ClientsSelection } from 'src/components';
import { DialogActionProp } from 'src/store/typings';

import BaseDialog from './base';

function CopyDialog({ isOpen, modelId, tenant: sourceTenant, clients, actions }) {
  const defaultValues = {
    targetTenant: '',
    newName: '',
  };

  const { handleSubmit, errors, control, setValue, watch } = useForm({ defaultValues });
  const onSubmit = useCallback(
    (data) => {
      const inputData = data;
      inputData.newName = inputData.newName?.trim?.() ?? '';
      inputData.targetTenant = inputData.targetTenant?.trim?.() ?? '';
      const formData = {
        modelId,
        sourceTenant,
        ...inputData,
      };
      actions.handlers[0].on(formData);
      actions.close.on();
    },
    [modelId, sourceTenant, actions.handlers, actions.close]
  );
  const formSubmit = handleSubmit(onSubmit);

  const handleTenantSelection = useCallback(
    (nextClient) => {
      setValue('targetTenant', nextClient.tenant, { shouldDirty: true });
    },
    [setValue]
  );
  const targetTenant = watch('targetTenant');

  return (
    <BaseDialog
      isOpen={isOpen}
      actions={{
        ...actions,
        handlers: [
          {
            on: formSubmit,
          },
        ],
      }}
      title={{
        text: `Copy Model`,
        backgroundColor: 'talLime',
        color: 'white',
        textSize: 'title',
        icon: <Icon name="Copy" color="white" viewBox="0 0 14 14" size="3.125rem" m={{ r: '1rem' }} />,
      }}
      items={{
        content: [
          <Div key="input-0" d="flex" flexDir="column">
            <Text textSize="input" textWeight="500" textColor="shade66" m={{ t: '1.25rem' }}>
              Optionally, enter the new name of this model
            </Text>
            <Controller
              borderColor="shade66"
              name="newName"
              as={Input}
              control={control}
              rules={{
                pattern: {
                  value: /^[^0-9\s\W]/,
                  message: 'Name must start with a letter - not a number, special character, or space.',
                },
              }}
              m={{ t: '.25rem' }}
              h="1.875rem"
            />
            <ErrorMessage
              errors={errors}
              name="newName"
              render={({ message }) => {
                if (![undefined, null, ''].includes(message)) {
                  return (
                    <Text textColor="danger800" textSize="caption" m={{ t: '.125rem' }}>
                      {message}
                    </Text>
                  );
                }
                return null;
              }}
            />
          </Div>,
          <Div key="input-1" d="flex" flexDir="column">
            <Text textSize="input" textWeight="500" textColor="shade66" m={{ t: '1.25rem', b: '.25rem' }}>
              Select the tenant to copy the model to
            </Text>
            <Controller
              borderColor="shade66"
              name="targetTenant"
              as={ClientsSelection}
              control={control}
              rules={{
                required: 'The tenant is required',
              }}
              h="1.875rem"
              w="100%"
              loading={false}
              clients={clients}
              tenant={targetTenant}
              defaultText="Please select a client"
              onSelectClient={handleTenantSelection}
            />
            <ErrorMessage
              errors={errors}
              name="targetTenant"
              render={({ message }) => {
                if (![undefined, null, ''].includes(message)) {
                  return (
                    <Text textColor="danger800" textSize="caption" m={{ t: '.125rem' }}>
                      {message}
                    </Text>
                  );
                }
                return null;
              }}
            />
          </Div>,
          <Div data-test="last-spacer" h="1.875rem" m={{ t: '1.25rem' }} />,
        ],
        buttons: [
          {
            text: 'YES',
            textSize: 'body',
            close: false,
          },
        ],
      }}
    />
  );
}

/* eslint-disable react/require-default-props */
CopyDialog.propTypes = {
  tenant: PropTypes.string,
  clients: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  user: PropTypes.shape({
    username: PropTypes.string,
    email: PropTypes.string,
    givenName: PropTypes.string,
    familyName: PropTypes.string,
    address: PropTypes.string,
    team: PropTypes.string,
    isActive: PropTypes.bool,
    userStatus: PropTypes.string,
    lastLoginAt: PropTypes.string,
    siteRole: PropTypes.shape({
      name: PropTypes.string,
    }),
  }).isRequired,
  modelId: PropTypes.number,
  isOpen: PropTypes.bool.isRequired,
  actions: DialogActionProp.isRequired,
  title: PropTypes.shape({
    text: PropTypes.string.isRequired,
    textSize: PropTypes.string,
    backgroundColor: PropTypes.string.isRequired,
    color: PropTypes.string,
  }),
};
/* eslint-enable */

export default CopyDialog;
